import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { literatureApi } from "../../api/literatureApi";
import { podcastsApi } from "../../api/podcastsApi";
import { psychologistTestsApi } from "../../api/psychologistTestsApi";
import { useCreateSectionGate } from "../../subscription/useCreateSectionGate";

const TABS = [
  {
    key: "literature",
    label: "Литература",
    shortLabel: "Книги",
    icon: "📚",
    createTo: "/dashboard/literature/new",
    hint: "Поиск по всей литературе проекта",
  },
  {
    key: "podcast",
    label: "Подкасты",
    shortLabel: "Подкасты",
    icon: "🎙️",
    createTo: "/dashboard/podcasts/new",
    hint: "Поиск по всем подкастам проекта",
  },
  {
    key: "test",
    label: "Тесты",
    shortLabel: "Тесты",
    icon: "📝",
    createTo: "/dashboard/tests/new",
    hint: "Поиск по доступным тестам проекта",
  },
];

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.item?.items)) return payload.item.items;
  if (Array.isArray(payload?.item?.content)) return payload.item.content;
  if (Array.isArray(payload?.item)) return payload.item;
  return [];
}

function pick(value, fallback = "") {
  return String(value ?? fallback ?? "").trim();
}

function short(value, max = 190) {
  const s = pick(value);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function idOf(item) {
  return item?.id ?? item?.bookId ?? item?.episodeId ?? item?.podcastId ?? item?.testId ?? item?.slug ?? item?.uuid ?? "";
}

function normalizeBook(item) {
  const book = item?.book && typeof item.book === "object" ? item.book : item;
  const id = idOf(book);
  const slugOrId = book?.slug || id;
  return {
    type: "literature",
    id,
    title: pick(book?.title || book?.name, "Книга без названия"),
    subtitle: pick(book?.author || book?.authors || book?.topic || book?.category, "Литература"),
    description: short(book?.description || book?.summary || item?.note || item?.comment || ""),
    url: slugOrId ? `/literature/books/${encodeURIComponent(String(slugOrId))}` : "/literature",
    actionLabel: "Открыть книгу",
    sourceLabel: item?.mine ? "Моё" : "Проект",
  };
}

function normalizePodcast(item) {
  const episode = item?.episode && typeof item.episode === "object" ? item.episode : item;
  const id = idOf(episode);
  const slugOrId = episode?.slug || id;
  return {
    type: "podcast",
    id,
    title: pick(episode?.title || episode?.name, "Подкаст без названия"),
    subtitle: pick(episode?.authorName || episode?.psychologistName || episode?.topic || episode?.category, "Подкаст"),
    description: short(episode?.description || episode?.summary || episode?.shortDescription || ""),
    url: slugOrId ? `/podcasts/${encodeURIComponent(String(slugOrId))}` : "/podcasts",
    actionLabel: "Открыть подкаст",
    imageUrl: episode?.coverUrl || episode?.imageUrl || episode?.cover || "",
    sourceLabel: item?.mine ? "Моё" : "Проект",
  };
}

function normalizeTest(item) {
  const test = item?.test && typeof item.test === "object" ? item.test : item;
  const id = idOf(test);
  return {
    type: "test",
    id,
    title: pick(test?.title || test?.name, "Тест без названия"),
    subtitle: [test?.topic || test?.category, test?.estimatedMinutes ? `${test.estimatedMinutes} мин` : ""]
      .map((x) => pick(x))
      .filter(Boolean)
      .join(" · ") || "Психологический тест",
    description: short(test?.description || test?.summary || test?.instructions || ""),
    url: test?.publicUrl || (id ? `/tests/${encodeURIComponent(String(id))}` : "/dashboard#cd-test-section"),
    actionLabel: "Открыть тест",
    sourceLabel: item?.mine ? "Моё" : "Проект",
  };
}

function dedupe(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = `${item.type}:${item.id || item.title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

async function loadTab(tab, query) {
  const q = String(query || "").trim();

  if (tab === "literature") {
    const attempts = [
      () => literatureApi.getCatalog({ q, page: 0, size: 40 }),
      () => literatureApi.getFeatured({ page: 0, size: 40 }),
      () => literatureApi.getMyShelf({ tab: "ALL", page: 0, size: 40 }),
      () => literatureApi.getMyBooks({ page: 0, size: 40 }),
    ];
    for (const attempt of attempts) {
      try {
        const raw = await attempt();
        const items = asArray(raw).map(normalizeBook).filter((x) => x.id || x.title);
        if (items.length) return dedupe(items);
      } catch {
        // try next endpoint
      }
    }
    return [];
  }

  if (tab === "podcast") {
    const attempts = [
      () => podcastsApi.getCatalog({ q, page: 0, size: 40 }),
      () => podcastsApi.getMyPodcasts({ page: 0, size: 40 }),
    ];
    for (const attempt of attempts) {
      try {
        const raw = await attempt();
        const items = asArray(raw).map(normalizePodcast).filter((x) => x.id || x.title);
        if (items.length) return dedupe(items);
      } catch {
        // try next endpoint
      }
    }
    return [];
  }

  const attempts = [
    () => psychologistTestsApi.searchAll?.({ q, page: 0, size: 40 }),
    () => psychologistTestsApi.listPublic?.({ q, page: 0, size: 40 }),
    () => psychologistTestsApi.listMine({ page: 0, size: 40 }),
  ];
  for (const attempt of attempts) {
    if (typeof attempt !== "function") continue;
    try {
      const raw = await attempt();
      const items = asArray(raw).map(normalizeTest).filter((x) => x.id || x.title);
      if (items.length) return dedupe(items);
    } catch {
      // try next endpoint
    }
  }
  return [];
}

export default function ChatRecommendationPicker({ open, onClose, onSend, disabled = false }) {
  const navigate = useNavigate();
  const { checkCreateAccess, limitModal } = useCreateSectionGate();
  const [tab, setTab] = React.useState("literature");
  const [query, setQuery] = React.useState("");
  const [itemsByTab, setItemsByTab] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [sendingKey, setSendingKey] = React.useState("");

  const currentTab = TABS.find((item) => item.key === tab) || TABS[0];
  const cacheKey = `${tab}:${query.trim().toLowerCase()}`;
  const items = itemsByTab[cacheKey] || [];

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const next = await loadTab(tab, query);
        if (!cancelled) {
          setItemsByTab((prev) => ({ ...prev, [cacheKey]: next }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Не удалось загрузить материалы");
          setItemsByTab((prev) => ({ ...prev, [cacheKey]: [] }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [cacheKey, open, query, tab]);

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      setSendingKey("");
    }
  }, [open]);

  const handleCreateClick = React.useCallback(
    async (event) => {
      event.preventDefault();
      const target = currentTab?.createTo || "/dashboard";
      const allowed = await checkCreateAccess(target);
      if (!allowed) return;
      onClose?.();
      navigate(target);
    },
    [checkCreateAccess, currentTab?.createTo, navigate, onClose]
  );

  if (!open) return null;

  return (
    <div className="chat-recommend-picker" onClick={(event) => event.stopPropagation()}>
      <div className="chat-recommend-picker__head">
        <div>
          <div className="chat-recommend-picker__eyebrow">Материалы проекта</div>
          <div className="chat-recommend-picker__title">Предложить в чат</div>
          <div className="chat-recommend-picker__subtitle">Найдите книгу, подкаст или тест и отправьте собеседнику карточкой.</div>
        </div>
        <button type="button" className="chat-recommend-picker__close" onClick={onClose} aria-label="Закрыть">
          ×
        </button>
      </div>

      <div className="chat-recommend-picker__tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`chat-recommend-picker__tab ${tab === item.key ? "active" : ""}`}
            onClick={() => setTab(item.key)}
            title={item.hint}
          >
            <span>{item.icon}</span>
            {item.shortLabel}
          </button>
        ))}
      </div>

      <div className="chat-recommend-picker__tools">
        <div className="chat-recommend-picker__search">
          <span>⌕</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Поиск по проекту: ${currentTab.label.toLowerCase()}...`}
          />
        </div>
        <Link className="chat-recommend-picker__create" to={currentTab.createTo} onClick={handleCreateClick}>
          Добавить
        </Link>
      </div>

      <div className="chat-recommend-picker__body">
        {loading ? <div className="chat-recommend-picker__state">Ищу материалы...</div> : null}
        {!loading && error ? <div className="chat-recommend-picker__state chat-recommend-picker__state--error">{error}</div> : null}
        {!loading && !error && !items.length ? (
          <div className="chat-recommend-picker__state">
            Ничего не найдено. Попробуйте другой запрос или добавьте материал в кабинет.
          </div>
        ) : null}

        {!loading && !error && items.map((item) => {
          const key = `${item.type}:${item.id || item.title}`;
          const isSending = sendingKey === key;
          return (
            <div className="chat-recommend-picker__item" key={key}>
              <div className="chat-recommend-picker__item-icon">
                {item.type === "literature" ? "📚" : item.type === "podcast" ? "🎙️" : "📝"}
              </div>
              <div className="chat-recommend-picker__item-main">
                <div className="chat-recommend-picker__item-title">{item.title}</div>
                {item.subtitle ? <div className="chat-recommend-picker__item-subtitle">{item.subtitle}</div> : null}
                {item.description ? <div className="chat-recommend-picker__item-desc">{item.description}</div> : null}
                <div className="chat-recommend-picker__item-source">{item.sourceLabel || "Проект"}</div>
              </div>
              <button
                type="button"
                className="chat-recommend-picker__send"
                disabled={disabled || isSending}
                onClick={async () => {
                  setSendingKey(key);
                  try {
                    await onSend(item);
                    onClose?.();
                  } finally {
                    setSendingKey("");
                  }
                }}
              >
                {isSending ? "..." : "В чат"}
              </button>
            </div>
          );
        })}
      </div>
      {limitModal}
    </div>
  );
}
